// pages_modelreq.js — AI Model Request System
// Client: submits requirement form → SuperAdmin gets email → reviews & quotes
import React, { useState, useEffect } from 'react';
import { Badge, Btn, Inp, Sel, Textarea, Modal, Toast, PageHeader, Panel, KPI } from './utils';

const T = {
  accent:"#C48A0E", gold:"#C48A0E", bg:"#F0F2F7", card:"#FFFFFF",
  panel:"#FFFFFF", deep:"#0A1628", text:"#1A2235", muted:"#8492A6",
  border:"#E2E8F0", faint:"rgba(26,34,53,0.04)", blue:"#3B82F6",
  green:"#22C55E", red:"#EF4444", orange:"#F97316", purple:"#7C3AED",
  font:"'DM Sans','Segoe UI',system-ui,sans-serif", mono:"'IBM Plex Mono',monospace",
  hi:"#94A3B8",
};

const API_NODE = (typeof process!=='undefined'&&process.env?.REACT_APP_API_URL)
  ? process.env.REACT_APP_API_URL : '';

const apiFetch = async (path) => {
  const token = localStorage.getItem('auravex_token');
  const res = await fetch(API_NODE + path, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`API ${res.status}`);
  return res.json();
};

const apiPost = async (path, body) => {
  const token = localStorage.getItem('auravex_token');
  const res = await fetch(API_NODE + path, {
    method:'POST', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
    body: JSON.stringify(body)
  });
  return res.json();
};

const apiPatch = async (path, body) => {
  const token = localStorage.getItem('auravex_token');
  const res = await fetch(API_NODE + path, {
    method:'PATCH', headers:{'Content-Type':'application/json', Authorization:`Bearer ${token}`},
    body: JSON.stringify(body)
  });
  return res.json();
};

const STATUS_COLORS = {
  Pending:'#F97316', Reviewing:'#7C3AED', Quoted:'#3B82F6',
  'In Progress':'#F59E0B', Completed:'#22C55E', Rejected:'#EF4444'
};

/* ================================================================
   CLIENT PAGE — Submit AI Model Request
================================================================ */
export const PageMyModelRequests = ({ loginUser, models, toast }) => {
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    use_case:'', camera_count:'1', environment:'', objects:'',
    alerts:'', priority:'Normal', budget:'', notes:'',
    contact_name: loginUser?.name || '',
    contact_email: loginUser?.email || '',
    contact_mobile: '',
  });
  const ff = k => v => setForm(p=>({...p,[k]:v}));

  useEffect(() => {
    setLoading(true);
    apiFetch('/api/model-requests')
      .then(setRequests)
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, []);

  const submit = async () => {
    if (!form.use_case.trim()) { toast('Please describe your use case', 'warn'); return; }
    setSubmitting(true);
    try {
      const res = await apiPost('/api/model-requests', {...form, camera_count: parseInt(form.camera_count)||1});
      if (res.error) { toast(res.error, 'warn'); return; }
      setRequests(p => [res.request, ...p]);
      setShowForm(false);
      setForm(p => ({...p, use_case:'', objects:'', alerts:'', notes:'', budget:''}));
      toast('✅ Request submitted! AURAVEX team will contact you shortly.', 'success');
    } catch(e) { toast('Submit failed: ' + e.message, 'warn'); }
    setSubmitting(false);
  };

  const ENVS = ['Indoor','Outdoor','Both','Warehouse','Factory','Retail','Hospital','Smart City','Highway','Parking'];
  const PRIORITIES = ['Normal','High','Urgent'];

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <PageHeader
        title="🔬 Request Custom AI Model"
        sub="Tell us your requirements — our AI team will build and deploy a custom model for your cameras">
        <Btn onClick={()=>setShowForm(true)}>+ New Request</Btn>
      </PageHeader>

      {/* Info banner */}
      <div style={{background:'linear-gradient(135deg,rgba(196,138,14,0.12),rgba(196,138,14,0.04))',
        border:'1px solid rgba(196,138,14,0.3)',borderRadius:12,padding:'16px 20px',
        display:'flex',gap:16,alignItems:'flex-start'}}>
        <div style={{fontSize:32,flexShrink:0}}>🤖</div>
        <div style={{flex:1}}>
          <div style={{fontSize:14,fontWeight:800,color:T.gold,marginBottom:6}}>Custom AI Model Development</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginTop:8}}>
            {[
              ['📋','Submit Requirements','Describe your use case, camera setup, and detection needs'],
              ['💬','Get a Quote','Our AI team reviews and sends you a custom price quote'],
              ['🚀','Model Deployed','Custom model trained and deployed to your cameras'],
            ].map(([icon,title,desc])=>(
              <div key={title} style={{background:'rgba(255,255,255,0.6)',borderRadius:8,padding:'10px 12px'}}>
                <div style={{fontSize:18,marginBottom:4}}>{icon}</div>
                <div style={{fontSize:11,fontWeight:700,color:T.text,marginBottom:2}}>{title}</div>
                <div style={{fontSize:10,color:T.muted,lineHeight:1.4}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* My Requests */}
      <Panel title="MY REQUESTS">
        {loading ? (
          <div style={{textAlign:'center',padding:40,color:T.muted}}>Loading...</div>
        ) : requests.length === 0 ? (
          <div style={{textAlign:'center',padding:40}}>
            <div style={{fontSize:40,marginBottom:12}}>🔬</div>
            <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>No requests yet</div>
            <div style={{fontSize:12,color:T.muted,marginBottom:16}}>Submit your first AI model requirement to get started</div>
            <Btn onClick={()=>setShowForm(true)}>+ Submit Requirement</Btn>
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {requests.map(r => (
              <div key={r.id} style={{background:T.faint,borderRadius:10,padding:'14px 16px',
                border:`1px solid ${T.border}`,display:'flex',gap:14,alignItems:'flex-start'}}>
                <div style={{width:40,height:40,borderRadius:10,background:`${STATUS_COLORS[r.status]||T.muted}18`,
                  border:`1px solid ${STATUS_COLORS[r.status]||T.muted}44`,display:'flex',alignItems:'center',
                  justifyContent:'center',fontSize:18,flexShrink:0}}>🔬</div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
                    <span style={{fontSize:13,fontWeight:700,color:T.text}}>Request #{r.id}</span>
                    <span style={{fontSize:10,padding:'2px 8px',borderRadius:12,fontWeight:700,
                      background:`${STATUS_COLORS[r.status]||T.muted}18`,
                      color:STATUS_COLORS[r.status]||T.muted}}>{r.status}</span>
                    <span style={{fontSize:10,padding:'2px 7px',borderRadius:12,
                      background:`${r.priority==='Urgent'?T.red:r.priority==='High'?T.orange:T.blue}18`,
                      color:r.priority==='Urgent'?T.red:r.priority==='High'?T.orange:T.blue,fontWeight:700}}>
                      {r.priority}
                    </span>
                    <span style={{fontSize:10,color:T.muted,marginLeft:'auto'}}>
                      {new Date(r.created_at).toLocaleDateString('en-IN')}
                    </span>
                  </div>
                  <div style={{fontSize:12,color:T.text,marginBottom:4,lineHeight:1.5}}>{r.use_case}</div>
                  <div style={{display:'flex',gap:16,flexWrap:'wrap'}}>
                    <span style={{fontSize:10,color:T.muted}}>📹 {r.camera_count} camera(s)</span>
                    {r.environment&&<span style={{fontSize:10,color:T.muted}}>📍 {r.environment}</span>}
                    {r.budget&&<span style={{fontSize:10,color:T.muted}}>💰 Budget: {r.budget}</span>}
                  </div>
                  {r.quoted_price&&(
                    <div style={{marginTop:8,padding:'8px 12px',background:'rgba(34,197,94,0.1)',
                      border:'1px solid rgba(34,197,94,0.3)',borderRadius:6,fontSize:12,color:T.green,fontWeight:700}}>
                      💰 Quoted Price: ₹{parseFloat(r.quoted_price).toLocaleString('en-IN')}
                    </div>
                  )}
                  {r.admin_notes&&(
                    <div style={{marginTop:8,padding:'8px 12px',background:'rgba(59,130,246,0.08)',
                      border:'1px solid rgba(59,130,246,0.2)',borderRadius:6,fontSize:11,color:T.blue}}>
                      💬 <strong>AURAVEX Team:</strong> {r.admin_notes}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Submit Form Modal */}
      <Modal open={showForm} title="🔬 Submit AI Model Requirement" onClose={()=>setShowForm(false)}>
        <div style={{display:'flex',flexDirection:'column',gap:12,maxHeight:'60vh',overflowY:'auto',paddingRight:4}}>

          <div style={{padding:'10px 12px',background:'rgba(196,138,14,0.08)',border:'1px solid rgba(196,138,14,0.2)',
            borderRadius:8,fontSize:11,color:T.gold,lineHeight:1.5}}>
            💡 Describe your requirement in detail. Our AI team will review, build, and deploy a custom model for your cameras. Pricing is based on complexity.
          </div>

          <Textarea label="USE CASE / REQUIREMENT *"
            value={form.use_case}
            onChange={ff('use_case')}
            placeholder="e.g. Detect workers not wearing helmets and safety vests in a construction site. Alert when more than 2 people without PPE are in frame simultaneously."
            rows={4}/>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <Inp label="NUMBER OF CAMERAS" type="number" value={form.camera_count}
              onChange={ff('camera_count')}/>
            <Sel label="ENVIRONMENT" value={form.environment} onChange={ff('environment')}
              options={[{v:'',l:'Select...'}, ...ENVS.map(e=>({v:e,l:e}))]}/>
          </div>

          <Inp label="OBJECTS TO DETECT" value={form.objects} onChange={ff('objects')}
            placeholder="e.g. Persons, vehicles, animals, fire, smoke, PPE items..."/>

          <Inp label="ALERT CONDITIONS" value={form.alerts} onChange={ff('alerts')}
            placeholder="e.g. Alert when person enters restricted zone, when speed > 60km/h..."/>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
            <Sel label="PRIORITY" value={form.priority} onChange={ff('priority')}
              options={PRIORITIES.map(p=>({v:p,l:p}))}/>
            <Inp label="BUDGET (optional)" value={form.budget}
              onChange={ff('budget')}
              placeholder="e.g. ₹50,000 or flexible"/>
          </div>

          <Textarea label="ADDITIONAL NOTES" value={form.notes} onChange={ff('notes')}
            placeholder="Any other details — camera brand, lighting conditions, accuracy needs..."
            rows={2}/>

          <div style={{borderTop:`1px solid ${T.border}`,paddingTop:12,marginTop:4}}>
            <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8,letterSpacing:1}}>YOUR CONTACT DETAILS</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <Inp label="NAME" value={form.contact_name} onChange={ff('contact_name')}/>
              <Inp label="MOBILE" value={form.contact_mobile} onChange={ff('contact_mobile')}
                placeholder="+91 98000 00000"/>
            </div>
            <Inp label="EMAIL" value={form.contact_email} onChange={ff('contact_email')}
              style={{marginTop:10}}/>
          </div>

          <div style={{display:'flex',gap:8,justifyContent:'flex-end',paddingTop:4}}>
            <Btn v="ghost" onClick={()=>setShowForm(false)}>Cancel</Btn>
            <Btn onClick={submit} disabled={submitting}>
              {submitting ? 'Submitting...' : '🚀 Submit Requirement'}
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
};

/* ================================================================
   SUPERADMIN PAGE — View & Manage all AI Model Requests
================================================================ */
export const PageModelRequests = ({ models, tenants, toast }) => {
  const [requests,  setRequests]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [filterSt,  setFilterSt]  = useState('All');
  const [adminForm, setAdminForm] = useState({ status:'', quoted_price:'', admin_notes:'', assigned_model:'' });

  useEffect(() => {
    apiFetch('/api/model-requests')
      .then(setRequests)
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, []);

  const updateRequest = async () => {
    if (!selected) return;
    try {
      const res = await apiPatch(`/api/model-requests/${selected.id}`, adminForm);
      if (res.error) { toast(res.error, 'warn'); return; }
      setRequests(p => p.map(r => r.id === selected.id ? res.request : r));
      setSelected(null);
      toast('✅ Request updated — client notified by email', 'success');
    } catch(e) { toast('Update failed: ' + e.message, 'warn'); }
  };

  const STATUSES = ['All','Pending','Reviewing','Quoted','In Progress','Completed','Rejected'];
  const filtered = filterSt === 'All' ? requests : requests.filter(r=>r.status===filterSt);

  const counts = STATUSES.slice(1).reduce((a,s)=>({...a,[s]:requests.filter(r=>r.status===s).length}),{});

  return (
    <div style={{display:'flex',flexDirection:'column',gap:16}}>
      <PageHeader title="🔬 AI Model Requests" sub="Client requirements — review, quote, assign models and notify clients">
        <div style={{fontSize:12,color:T.muted,fontWeight:600}}>{requests.length} total requests</div>
      </PageHeader>

      {/* KPI Row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10}}>
        <KPI label="Total"       value={requests.length}              icon="🔬" color={T.blue}/>
        <KPI label="Pending"     value={counts['Pending']||0}          icon="⏳" color={T.orange}/>
        <KPI label="Reviewing"   value={counts['Reviewing']||0}        icon="👁" color={T.purple}/>
        <KPI label="Quoted"      value={counts['Quoted']||0}           icon="💰" color={T.blue}/>
        <KPI label="Completed"   value={counts['Completed']||0}        icon="✅" color={T.green}/>
      </div>

      {/* Filter pills */}
      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
        {STATUSES.map(s=>(
          <button key={s} onClick={()=>setFilterSt(s)}
            style={{padding:'5px 12px',borderRadius:20,fontSize:11,fontWeight:700,cursor:'pointer',
              background:filterSt===s?`${STATUS_COLORS[s]||T.blue}18`:'transparent',
              border:`1px solid ${filterSt===s?(STATUS_COLORS[s]||T.blue):T.border}`,
              color:filterSt===s?(STATUS_COLORS[s]||T.blue):T.muted}}>
            {s}{s!=='All'&&counts[s]?` (${counts[s]})`:s==='All'?` (${requests.length})`:''}
          </button>
        ))}
      </div>

      {/* Requests Table */}
      <Panel title="ALL REQUESTS">
        {loading ? (
          <div style={{textAlign:'center',padding:40,color:T.muted}}>Loading requests...</div>
        ) : filtered.length === 0 ? (
          <div style={{textAlign:'center',padding:40,color:T.muted}}>
            <div style={{fontSize:32,marginBottom:8}}>📭</div>
            {filterSt==='All' ? 'No requests received yet' : `No ${filterSt} requests`}
          </div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {filtered.map(r=>(
              <div key={r.id} onClick={()=>{setSelected(r);setAdminForm({status:r.status,quoted_price:r.quoted_price||'',admin_notes:r.admin_notes||'',assigned_model:r.assigned_model||''});}}
                style={{background:T.faint,borderRadius:10,padding:'13px 16px',border:`1px solid ${T.border}`,
                  cursor:'pointer',transition:'all .15s',display:'flex',gap:12,alignItems:'flex-start'}}
                onMouseEnter={e=>e.currentTarget.style.borderColor=T.accent}
                onMouseLeave={e=>e.currentTarget.style.borderColor=T.border}>

                <div style={{width:36,height:36,borderRadius:8,background:`${STATUS_COLORS[r.status]||T.muted}18`,
                  border:`1px solid ${STATUS_COLORS[r.status]||T.muted}33`,display:'flex',alignItems:'center',
                  justifyContent:'center',fontSize:16,flexShrink:0}}>🔬</div>

                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3,flexWrap:'wrap'}}>
                    <span style={{fontSize:12,fontWeight:800,color:T.text}}>#{r.id}</span>
                    <span style={{fontSize:11,fontWeight:700,color:T.text}}>{r.tenant_name}</span>
                    <span style={{fontSize:10,padding:'2px 7px',borderRadius:10,fontWeight:700,
                      background:`${STATUS_COLORS[r.status]||T.muted}18`,color:STATUS_COLORS[r.status]||T.muted}}>
                      {r.status}
                    </span>
                    <span style={{fontSize:10,padding:'2px 7px',borderRadius:10,fontWeight:700,
                      background:`${r.priority==='Urgent'?T.red:r.priority==='High'?T.orange:T.blue}12`,
                      color:r.priority==='Urgent'?T.red:r.priority==='High'?T.orange:T.blue}}>
                      {r.priority}
                    </span>
                    <span style={{fontSize:10,color:T.muted,marginLeft:'auto'}}>
                      {new Date(r.created_at).toLocaleDateString('en-IN')}
                    </span>
                  </div>
                  <div style={{fontSize:12,color:T.text,lineHeight:1.4,marginBottom:3}}>
                    {r.use_case.length > 120 ? r.use_case.slice(0,120)+'...' : r.use_case}
                  </div>
                  <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
                    <span style={{fontSize:10,color:T.muted}}>👤 {r.contact_name}</span>
                    <span style={{fontSize:10,color:T.muted}}>📧 {r.contact_email}</span>
                    <span style={{fontSize:10,color:T.muted}}>📹 {r.camera_count} cam(s)</span>
                    {r.budget&&<span style={{fontSize:10,color:T.muted}}>💰 {r.budget}</span>}
                    {r.quoted_price&&<span style={{fontSize:10,color:T.green,fontWeight:700}}>Quoted: ₹{parseFloat(r.quoted_price).toLocaleString('en-IN')}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* Review Modal */}
      <Modal open={!!selected} title={`🔬 Review Request #${selected?.id} — ${selected?.tenant_name}`} onClose={()=>setSelected(null)}>
        {selected&&(
          <div style={{display:'flex',flexDirection:'column',gap:12,maxHeight:'65vh',overflowY:'auto',paddingRight:4}}>

            {/* Client Details */}
            <div style={{background:T.faint,borderRadius:8,padding:'12px 14px',border:`1px solid ${T.border}`}}>
              <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:8,letterSpacing:1}}>CLIENT DETAILS</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,fontSize:12}}>
                <div><span style={{color:T.muted}}>Name: </span><strong>{selected.contact_name}</strong></div>
                <div><span style={{color:T.muted}}>Mobile: </span><strong>{selected.contact_mobile||'—'}</strong></div>
                <div style={{gridColumn:'1/-1'}}><span style={{color:T.muted}}>Email: </span><strong>{selected.contact_email}</strong></div>
                <div><span style={{color:T.muted}}>Cameras: </span><strong>{selected.camera_count}</strong></div>
                <div><span style={{color:T.muted}}>Environment: </span><strong>{selected.environment||'—'}</strong></div>
                <div><span style={{color:T.muted}}>Priority: </span><strong>{selected.priority}</strong></div>
                <div><span style={{color:T.muted}}>Budget: </span><strong>{selected.budget||'Not specified'}</strong></div>
              </div>
            </div>

            {/* Requirement Details */}
            <div style={{background:'rgba(196,138,14,0.06)',borderRadius:8,padding:'12px 14px',border:'1px solid rgba(196,138,14,0.2)'}}>
              <div style={{fontSize:11,fontWeight:700,color:T.gold,marginBottom:6,letterSpacing:1}}>USE CASE / REQUIREMENT</div>
              <div style={{fontSize:12,color:T.text,lineHeight:1.6}}>{selected.use_case}</div>
              {selected.objects&&<div style={{marginTop:8}}><span style={{fontSize:10,fontWeight:700,color:T.muted}}>OBJECTS: </span><span style={{fontSize:12}}>{selected.objects}</span></div>}
              {selected.alerts&&<div style={{marginTop:4}}><span style={{fontSize:10,fontWeight:700,color:T.muted}}>ALERTS: </span><span style={{fontSize:12}}>{selected.alerts}</span></div>}
              {selected.notes&&<div style={{marginTop:4}}><span style={{fontSize:10,fontWeight:700,color:T.muted}}>NOTES: </span><span style={{fontSize:12}}>{selected.notes}</span></div>}
            </div>

            {/* Admin Actions */}
            <div style={{borderTop:`1px solid ${T.border}`,paddingTop:12}}>
              <div style={{fontSize:11,fontWeight:700,color:T.muted,marginBottom:10,letterSpacing:1}}>UPDATE REQUEST</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <Sel label="STATUS" value={adminForm.status}
                  onChange={v=>setAdminForm(p=>({...p,status:v}))}
                  options={['Pending','Reviewing','Quoted','In Progress','Completed','Rejected'].map(s=>({v:s,l:s}))}/>
                <Inp label="QUOTED PRICE (₹)" type="number" value={adminForm.quoted_price}
                  onChange={v=>setAdminForm(p=>({...p,quoted_price:v}))}
                  placeholder="e.g. 75000"/>
              </div>
              <div style={{marginTop:10}}>
                <Sel label="ASSIGN AI MODEL (optional)" value={adminForm.assigned_model}
                  onChange={v=>setAdminForm(p=>({...p,assigned_model:v}))}
                  options={[{v:'',l:'None / Custom Build'}, ...(models||[]).map(m=>({v:m.id,l:`${m.id} — ${m.name}`}))]}/>
              </div>
              <div style={{marginTop:10}}>
                <Textarea label="MESSAGE TO CLIENT (sent via email)"
                  value={adminForm.admin_notes}
                  onChange={v=>setAdminForm(p=>({...p,admin_notes:v}))}
                  placeholder="e.g. We've reviewed your requirement. Custom PPE detector for 8 cameras, outdoor, day/night. Estimated delivery 3 weeks. Quote attached."
                  rows={3}/>
              </div>
            </div>

            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <Btn v="ghost" onClick={()=>setSelected(null)}>Cancel</Btn>
              <Btn onClick={updateRequest}>💾 Update & Notify Client</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};
